import React from 'react'
import { translate } from 'locales'
import { Button } from 'components'
import { CalendarToday, AccessTime, Edit } from '@mui/icons-material'
import moment from 'moment'
import 'moment/locale/pt-br'
import militaryTechIcon from 'assets/icons/military_tech.svg'
import * as S from './styles'

interface Appointment {
  id: string
  date: string
  time: string
  slotIds: string[]
  isFutureCredit: boolean
}

interface AppointmentsSelectionProps {
  appointmentsList: Appointment[]
  selectedAppointments: string[]
  selectedDuration: string
  editingAppointment: string | null
  editSelectedDate: string
  editSelectedTime: string
  editAvailableDates: string[]
  editAvailableTimes: any[]
  editWeekDates: string[]
  loadingEditTimes: boolean
  dayNames: string[]
  monthNames: string[]
  editCurrentMonth: Date
  onToggleAppointment: (id: string) => void
  onEditAppointment: (appointment: Appointment) => void
  onEditDateSelect: (date: string) => void
  onEditTimeSelect: (timeId: string) => void
  onSaveEdit: () => void
  onCancelEdit: () => void
  onConfirm: () => void
  getEditWeekDays: () => Array<{
    day: number
    date: string
    dayName: string
  }>
  isEditDateAvailable: (dateStr: string) => boolean
}

export const AppointmentsSelection: React.FC<AppointmentsSelectionProps> = ({
  appointmentsList,
  selectedAppointments,
  selectedDuration,
  editingAppointment,
  editSelectedDate,
  editSelectedTime,
  editAvailableDates,
  editAvailableTimes,
  editWeekDates,
  loadingEditTimes,
  dayNames,
  monthNames,
  editCurrentMonth,
  onToggleAppointment,
  onEditAppointment,
  onEditDateSelect,
  onEditTimeSelect,
  onSaveEdit,
  onCancelEdit,
  onConfirm,
  getEditWeekDays,
  isEditDateAvailable,
}) => {
  const firstFutureCreditIndex = appointmentsList.findIndex(
    (apt) => apt.isFutureCredit
  )

  return (
    <S.AppointmentsSelectionContainer>
      <S.AppointmentsHeader>
        <S.AppointmentsTitle>
          {translate('newAnalysis.appointments')}
        </S.AppointmentsTitle>
        <S.DurationInfo>
          <AccessTime style={{ fontSize: 18, marginRight: 8 }} />
          {translate('newAnalysis.duration')} {selectedDuration} min
        </S.DurationInfo>
      </S.AppointmentsHeader>

      <S.CurrentMonthText>
        {translate('newAnalysis.currentMonth')}
      </S.CurrentMonthText>

      <S.AppointmentsGrid>
        {appointmentsList.map((appointment, index) => {
          const isSelected = selectedAppointments.includes(appointment.id)
          const isFutureCredit = appointment.isFutureCredit
          const showBanner = index === firstFutureCreditIndex && isFutureCredit

          return (
            <React.Fragment key={appointment.id}>
              {showBanner && (
                <S.FutureCreditsBanner>
                  <img
                    src={militaryTechIcon}
                    alt="Military Tech"
                    className="banner-icon"
                  />
                  <span className="banner-text">
                    {translate('newAnalysis.referringToFutureCredits')}
                  </span>
                </S.FutureCreditsBanner>
              )}
              <div key={appointment.id}>
                <AppointmentCard
                  appointment={appointment}
                  isSelected={isSelected}
                  isEditing={editingAppointment === appointment.id}
                  editSelectedDate={editSelectedDate}
                  editSelectedTime={editSelectedTime}
                  editAvailableDates={editAvailableDates}
                  editAvailableTimes={editAvailableTimes}
                  editWeekDates={editWeekDates}
                  loadingEditTimes={loadingEditTimes}
                  dayNames={dayNames}
                  monthNames={monthNames}
                  editCurrentMonth={editCurrentMonth}
                  onToggle={() => onToggleAppointment(appointment.id)}
                  onEdit={() => onEditAppointment(appointment)}
                  onEditDateSelect={onEditDateSelect}
                  onEditTimeSelect={onEditTimeSelect}
                  onSaveEdit={onSaveEdit}
                  onCancelEdit={onCancelEdit}
                  getEditWeekDays={getEditWeekDays}
                  isEditDateAvailable={isEditDateAvailable}
                />
              </div>
            </React.Fragment>
          )
        })}
      </S.AppointmentsGrid>

      <S.ConfirmButtonContainer>
        <Button
          color="primary"
          type="button"
          onClick={onConfirm}
          disabled={selectedAppointments.length === 0}
        >
          {translate('newAnalysis.confirm')}
        </Button>
      </S.ConfirmButtonContainer>
    </S.AppointmentsSelectionContainer>
  )
}

interface AppointmentCardProps {
  appointment: Appointment
  isSelected: boolean
  isEditing: boolean
  editSelectedDate: string
  editSelectedTime: string
  editAvailableDates: string[]
  editAvailableTimes: any[]
  editWeekDates: string[]
  loadingEditTimes: boolean
  dayNames: string[]
  monthNames: string[]
  editCurrentMonth: Date
  onToggle: () => void
  onEdit: () => void
  onEditDateSelect: (date: string) => void
  onEditTimeSelect: (timeId: string) => void
  onSaveEdit: () => void
  onCancelEdit: () => void
  getEditWeekDays: () => Array<{
    day: number
    date: string
    dayName: string
  }>
  isEditDateAvailable: (dateStr: string) => boolean
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({
  appointment,
  isSelected,
  isEditing,
  editSelectedDate,
  editSelectedTime,
  editAvailableDates,
  editAvailableTimes,
  editWeekDates,
  loadingEditTimes,
  dayNames,
  monthNames,
  editCurrentMonth,
  onToggle,
  onEdit,
  onEditDateSelect,
  onEditTimeSelect,
  onSaveEdit,
  onCancelEdit,
  getEditWeekDays,
  isEditDateAvailable,
}) => {
  return (
    <>
      <S.AppointmentCard
        className={isSelected ? 'selected' : ''}
        onClick={onToggle}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          className="card-checkbox"
        />
        <S.CardContent>
          <S.CardSelectText>{translate('newAnalysis.select')}</S.CardSelectText>
          <S.CardInfoRow>
            <CalendarToday style={{ fontSize: 16, marginRight: 8 }} />
            <span className="info-label">{translate('newAnalysis.date')}:</span>
            <span className="info-value">
              {moment(appointment.date)
                .locale('pt-br')
                .format('ddd., D MMM YYYY')}
            </span>
          </S.CardInfoRow>
          <S.CardInfoRow>
            <AccessTime style={{ fontSize: 16, marginRight: 8 }} />
            <span className="info-label">{translate('newAnalysis.time')}:</span>
            <span className="info-value">
              {appointment.time}{' '}
              {parseInt(appointment.time.split(':')[0]) >= 12 ? 'PM' : 'AM'}
            </span>
          </S.CardInfoRow>
        </S.CardContent>
        <S.CardEditButton
          onClick={(e) => {
            e.stopPropagation()
            onEdit()
          }}
          disabled={isEditing}
        >
          <Edit style={{ fontSize: 14, marginRight: 4 }} />
          <span>{translate('newAnalysis.edit')}</span>
        </S.CardEditButton>
      </S.AppointmentCard>

      {isEditing && (
        <EditAppointmentInline
          editSelectedDate={editSelectedDate}
          editSelectedTime={editSelectedTime}
          editAvailableDates={editAvailableDates}
          editAvailableTimes={editAvailableTimes}
          editWeekDates={editWeekDates}
          loadingEditTimes={loadingEditTimes}
          dayNames={dayNames}
          monthNames={monthNames}
          editCurrentMonth={editCurrentMonth}
          onEditDateSelect={onEditDateSelect}
          onEditTimeSelect={onEditTimeSelect}
          onSaveEdit={onSaveEdit}
          onCancelEdit={onCancelEdit}
          getEditWeekDays={getEditWeekDays}
          isEditDateAvailable={isEditDateAvailable}
        />
      )}
    </>
  )
}

interface EditAppointmentInlineProps {
  editSelectedDate: string
  editSelectedTime: string
  editAvailableDates: string[]
  editAvailableTimes: any[]
  editWeekDates: string[]
  loadingEditTimes: boolean
  dayNames: string[]
  monthNames: string[]
  editCurrentMonth: Date
  onEditDateSelect: (date: string) => void
  onEditTimeSelect: (timeId: string) => void
  onSaveEdit: () => void
  onCancelEdit: () => void
  getEditWeekDays: () => Array<{
    day: number
    date: string
    dayName: string
  }>
  isEditDateAvailable: (dateStr: string) => boolean
}

const EditAppointmentInline: React.FC<EditAppointmentInlineProps> = ({
  editSelectedDate,
  editSelectedTime,
  editAvailableTimes,
  editWeekDates,
  loadingEditTimes,
  dayNames,
  monthNames,
  editCurrentMonth,
  onEditDateSelect,
  onEditTimeSelect,
  onSaveEdit,
  onCancelEdit,
  getEditWeekDays,
  isEditDateAvailable,
}) => {
  return (
    <S.EditInlineContainer>
      <S.EditCalendarSection>
        <label>{translate('sideModal.chooseDay')}</label>
        <div className="calendar">
          <div className="calendar-header">
            <h3 className="calendar-month">
              {editWeekDates.length > 0
                ? moment(editWeekDates[0]).locale('pt-br').format('MMMM YYYY')
                : monthNames[editCurrentMonth.getMonth()] +
                  ' ' +
                  editCurrentMonth.getFullYear()}
            </h3>
          </div>

          <div className="calendar-grid">
            {dayNames.map((day, index) => (
              <div key={index} className="calendar-day-header">
                {day}
              </div>
            ))}

            {getEditWeekDays().map((weekDay, index) => {
              const isAvailable = isEditDateAvailable(weekDay.date)
              const isSelected = editSelectedDate === weekDay.date

              return (
                <div
                  key={index}
                  className={`calendar-day ${isSelected ? 'selected' : ''} ${
                    isAvailable ? 'available' : 'disabled'
                  }`}
                  onClick={() => isAvailable && onEditDateSelect(weekDay.date)}
                >
                  {weekDay.day}
                </div>
              )
            })}
          </div>
        </div>
      </S.EditCalendarSection>

      {editSelectedDate && (
        <S.EditTimeSelection>
          <label>{translate('sideModal.chooseTime')}</label>
          {loadingEditTimes ? (
            <div className="loading-times">
              {translate('sideModal.loadingTimes')}
            </div>
          ) : (
            <div className="time-slots">
              {editAvailableTimes.length > 0 ? (
                editAvailableTimes.map((time: any, index: number) => {
                  const timeId = time?.slot_id
                  const timeDisplay = time?.time

                  return (
                    <button
                      key={timeId || index}
                      className={`time-slot ${
                        editSelectedTime === timeId ? 'selected' : ''
                      }`}
                      onClick={() => onEditTimeSelect(timeId)}
                    >
                      {timeDisplay}
                    </button>
                  )
                })
              ) : (
                <div className="no-times">
                  {translate('sideModal.noTimesAvailable')}
                </div>
              )}
            </div>
          )}
        </S.EditTimeSelection>
      )}

      <S.EditInlineFooter>
        <Button color="danger" type="button" onClick={onCancelEdit}>
          {translate('schedule.cancel')}
        </Button>
        <Button
          color="primary"
          type="button"
          onClick={onSaveEdit}
          disabled={!editSelectedDate || !editSelectedTime}
          styles={{
            backgroundColor: '#FFA947',
            color: '#FFFFFF',
          }}
        >
          {translate('button.save')}
        </Button>
      </S.EditInlineFooter>
    </S.EditInlineContainer>
  )
}
